/* Ares Acres — cart preview controller (spec §28–30, §35)
 * ---------------------------------------------------------------------------
 * Replaces the Finder/Doctor local "Added to cart" toast with a preview of the
 * CONFIRMED Shopify cart: real line items, real quantities, real totals, real
 * checkout.
 *
 * It attaches by observing successful POSTs to the cart-add route rather than
 * requiring the Finder and Doctor bundles to call it. That keeps this component
 * independent of those two files and means an add from anywhere on the page —
 * Finder result card, Doctor verified part, multi-item repair tray — lands in
 * the same preview through one code path (one cart, one event channel).
 *
 * Every state change is driven by a response from Shopify. Nothing here
 * simulates a cart, a price, a total or a checkout.
 *
 * States: idle · adding · confirming · open · mutating · error
 * Global: window.AresCartPreview
 */
(function () {
  'use strict';

  var el = document.getElementById('ares-cart-preview');
  if (!el) return;

  var ROUTES = {
    cart: el.getAttribute('data-cart-url') || '/cart',
    add: el.getAttribute('data-cart-add-url') || '/cart/add',
    change: el.getAttribute('data-cart-change-url') || '/cart/change',
    cartJs: el.getAttribute('data-cart-js-url') || '/cart.js'
  };
  var MONEY_FORMAT = el.getAttribute('data-money-format') || '${{amount}}';
  var HAS_ACCELERATED = el.getAttribute('data-has-accelerated') === 'true';

  var $ = function (s, r) { return (r || el).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || el).querySelectorAll(s)); };

  var S = {
    state: 'idle',
    reqId: 0,          // monotonic; stale responses are discarded
    inFlight: null,
    cart: null,
    lastFocus: null,
    pendingMutations: {}
  };

  /* ------------------------------------------------------------------ money */
  // Mirrors Shopify's money_format so the preview never shows a total that
  // disagrees with checkout. Cart JSON returns integer cents.
  function formatMoney(cents) {
    var n = (typeof cents === 'number' ? cents : parseInt(cents, 10) || 0) / 100;
    function withCommas(v, dec) {
      var parts = v.toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }
    var out = MONEY_FORMAT;
    if (/amount_no_decimals/.test(out)) return out.replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas(n, 0));
    if (/amount_with_comma_separator/.test(out)) {
      return out.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withCommas(n, 2).replace(/,/g, ' ').replace('.', ','));
    }
    return out.replace(/\{\{\s*amount\s*\}\}/, withCommas(n, 2));
  }

  /* ------------------------------------------------------------ live region */
  function say(msg, urgent) {
    var node = $(urgent ? '#acp-alert' : '#acp-live');
    if (node) { node.textContent = ''; setTimeout(function () { node.textContent = msg; }, 30); }
  }

  function setState(next) {
    S.state = next;
    el.setAttribute('data-state', next);
    var busy = next === 'adding' || next === 'confirming' || next === 'mutating';
    var panel = $('#acp-panel');
    if (panel) panel.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /* ------------------------------------------------------------ focus trap */
  function focusables() {
    return $$('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])', $('#acp-panel'))
      .filter(function (n) { return n.offsetParent !== null || n === document.activeElement; });
  }

  function onKeydown(e) {
    if (S.state !== 'open' && S.state !== 'mutating' && S.state !== 'error') return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;
    var f = focusables();
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  var scrollLock = { y: 0, on: false };
  function lockScroll(on) {
    if (on === scrollLock.on) return;
    scrollLock.on = on;
    if (on) {
      scrollLock.y = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = '-' + scrollLock.y + 'px';
      document.body.classList.add('acp-locked');
    } else {
      document.body.classList.remove('acp-locked');
      document.body.style.top = '';
      window.scrollTo(0, scrollLock.y);
    }
  }

  /* ------------------------------------------------------------ open/close */
  function open() {
    if (!el.hasAttribute('hidden')) return;
    S.lastFocus = document.activeElement;
    el.removeAttribute('hidden');
    lockScroll(true);
    document.addEventListener('keydown', onKeydown, true);
    setState('open');
    // Focus the dialog itself, not the first control, so a screen reader hears
    // the title and summary before the actions.
    var panel = $('#acp-panel');
    if (panel) panel.focus();
    track('cart_preview_opened');
  }

  function close() {
    if (el.hasAttribute('hidden')) return;
    el.setAttribute('hidden', '');
    lockScroll(false);
    document.removeEventListener('keydown', onKeydown, true);
    setState('idle');
    // Return focus to whatever opened it.
    if (S.lastFocus && document.contains(S.lastFocus)) { try { S.lastFocus.focus(); } catch (e) {} }
    S.lastFocus = null;
    track('cart_preview_closed');
  }

  /* ---------------------------------------------------------------- events */
  function track(name, props) {
    var p = props || {};
    p.event = name;
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, p); } catch (e) {}
    try { window.dataLayer = window.dataLayer || []; window.dataLayer.push(p); } catch (e) {}
  }

  /* ------------------------------------------------------------- rendering */
  function lineNode(item, index) {
    var wrap = document.createElement('div');
    wrap.className = 'acp__line';
    wrap.setAttribute('data-key', item.key);

    var img = document.createElement('div');
    img.className = 'acp__line-media';
    if (item.image) {
      var i = document.createElement('img');
      // Ask Shopify for a sized image rather than scaling a full-size one down.
      i.src = item.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2');
      i.alt = '';
      i.width = 80; i.height = 80; i.loading = 'lazy'; i.decoding = 'async';
      img.appendChild(i);
    }
    wrap.appendChild(img);

    var body = document.createElement('div');
    body.className = 'acp__line-body';

    var title = document.createElement('a');
    title.className = 'acp__line-title';
    title.href = item.url;
    title.textContent = item.product_title || item.title;
    body.appendChild(title);

    // Variant, when it is a real choice rather than the default single variant.
    if (item.variant_title && item.variant_title !== 'Default Title') {
      var v = document.createElement('p');
      v.className = 'acp__line-meta';
      v.textContent = item.variant_title;
      body.appendChild(v);
    }

    // The DJI material number is the operator's own identifier for the part.
    // It is the single most reassuring thing we can show at this moment, so it
    // gets its own line rather than being buried in the title.
    if (item.sku) {
      var sku = document.createElement('p');
      sku.className = 'acp__line-sku';
      sku.textContent = item.sku;
      body.appendChild(sku);
    }

    if (item.line_level_discount_allocations && item.line_level_discount_allocations.length) {
      item.line_level_discount_allocations.forEach(function (d) {
        var dd = document.createElement('p');
        dd.className = 'acp__line-disc';
        dd.textContent = d.discount_application.title + ' (−' + formatMoney(d.amount) + ')';
        body.appendChild(dd);
      });
    }

    var qtyRow = document.createElement('div');
    qtyRow.className = 'acp__qty';
    var dec = document.createElement('button');
    dec.type = 'button'; dec.className = 'acp__qty-btn'; dec.textContent = '−';
    dec.setAttribute('aria-label', 'Decrease quantity of ' + (item.product_title || item.title));
    var out = document.createElement('output');
    out.className = 'acp__qty-val'; out.textContent = item.quantity;
    var inc = document.createElement('button');
    inc.type = 'button'; inc.className = 'acp__qty-btn'; inc.textContent = '+';
    inc.setAttribute('aria-label', 'Increase quantity of ' + (item.product_title || item.title));
    dec.addEventListener('click', function () { mutate(item.key, item.quantity - 1, index); });
    inc.addEventListener('click', function () { mutate(item.key, item.quantity + 1, index); });
    qtyRow.appendChild(dec); qtyRow.appendChild(out); qtyRow.appendChild(inc);

    var rm = document.createElement('button');
    rm.type = 'button'; rm.className = 'acp__remove'; rm.textContent = 'Remove';
    rm.setAttribute('aria-label', 'Remove ' + (item.product_title || item.title) + ' from cart');
    rm.addEventListener('click', function () { mutate(item.key, 0, index); });
    qtyRow.appendChild(rm);
    body.appendChild(qtyRow);

    wrap.appendChild(body);

    var price = document.createElement('div');
    price.className = 'acp__line-price';
    if (item.original_line_price !== item.final_line_price) {
      var was = document.createElement('s');
      was.className = 'acp__line-was';
      was.textContent = formatMoney(item.original_line_price);
      price.appendChild(was);
    }
    var now = document.createElement('span');
    now.className = 'acp__line-now';
    now.textContent = formatMoney(item.final_line_price);
    price.appendChild(now);
    wrap.appendChild(price);

    return wrap;
  }

  function render(cart, addedKeys) {
    S.cart = cart;
    var lines = $('#acp-lines');
    lines.innerHTML = '';

    if (!cart || !cart.items || !cart.items.length) {
      var empty = document.createElement('p');
      empty.className = 'acp__empty';
      empty.textContent = 'Your cart is empty.';
      lines.appendChild(empty);
      $('#acp-checkout').disabled = true;
    } else {
      // Newly added lines first — the customer's attention is on what they just
      // did, not on what was already in the cart.
      var items = cart.items.slice();
      if (addedKeys && addedKeys.length) {
        items.sort(function (a, b) {
          return (addedKeys.indexOf(b.key) >= 0 ? 1 : 0) - (addedKeys.indexOf(a.key) >= 0 ? 1 : 0);
        });
      }
      items.forEach(function (it, i) {
        var n = lineNode(it, i);
        if (addedKeys && addedKeys.indexOf(it.key) >= 0) n.classList.add('is-added');
        lines.appendChild(n);
      });
      $('#acp-checkout').disabled = false;
    }

    var totals = $('#acp-totals');
    totals.innerHTML = '';
    if (cart) {
      if (cart.total_discount > 0) {
        totals.appendChild(row('Discounts', '−' + formatMoney(cart.total_discount), 'acp__row--disc'));
      }
      totals.appendChild(row(
        'Subtotal · ' + cart.item_count + (cart.item_count === 1 ? ' item' : ' items'),
        formatMoney(cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price),
        'acp__row--total'
      ));
    }

    var acc = $('#acp-accelerated');
    if (acc) acc.hidden = !cart || !cart.items || !cart.items.length;

    var sub = $('#acp-sub');
    if (sub && cart) {
      sub.textContent = cart.item_count
        ? 'Your Shopify cart now holds ' + cart.item_count + (cart.item_count === 1 ? ' item.' : ' items.')
        : 'Reviewed against your live Shopify cart.';
    }
  }

  function row(label, value, cls) {
    var r = document.createElement('div');
    r.className = 'acp__row' + (cls ? ' ' + cls : '');
    var l = document.createElement('span'); l.className = 'acp__row-k'; l.textContent = label;
    var v = document.createElement('span'); v.className = 'acp__row-v'; v.textContent = value;
    r.appendChild(l); r.appendChild(v);
    return r;
  }

  /* ---------------------------------------------------------------- errors */
  // Each error class gets a message the customer can act on, and never leaves
  // the dialog in a state where the button is dead.
  function fail(kind, detail) {
    setState('error');
    var lines = $('#acp-lines');
    var box = document.createElement('div');
    box.className = 'acp__error';
    var msg;
    if (kind === 'network') msg = 'We could not reach the cart. Check your connection and try again.';
    else if (kind === 'stock') msg = detail || 'That quantity is not available right now.';
    else if (kind === 'notfound') msg = 'That item is no longer available.';
    else msg = detail || 'Something went wrong updating your cart.';
    box.innerHTML = '<strong>Cart not updated</strong>';
    var p = document.createElement('p'); p.textContent = msg; box.appendChild(p);
    var retry = document.createElement('button');
    retry.type = 'button'; retry.className = 'acp__btn acp__btn--ghost'; retry.textContent = 'Reload cart';
    retry.addEventListener('click', function () { refresh(); });
    box.appendChild(retry);
    lines.insertBefore(box, lines.firstChild);
    say(msg, true);
    track('cart_preview_error', { error_class: kind });
  }

  /* ------------------------------------------------------------- cart reads */
  function refresh(addedKeys) {
    var id = ++S.reqId;
    setState('confirming');
    return fetch(ROUTES.cartJs, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('cart'); return r.json(); })
      .then(function (cart) {
        if (id !== S.reqId) return;           // a newer request superseded this one
        render(cart, addedKeys);
        setState('open');
        syncBubble(cart);
        return cart;
      })
      .catch(function () { if (id === S.reqId) fail('network'); });
  }

  function syncBubble(cart) {
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !cart) return;
    var count = bubble.querySelector('.cart-count-bubble span[aria-hidden]');
    if (count) count.textContent = cart.item_count;
  }

  /* --------------------------------------------------------- cart mutations */
  function mutate(key, quantity, index) {
    if (S.pendingMutations[key]) return;      // duplicate submission guard
    S.pendingMutations[key] = true;
    var id = ++S.reqId;
    setState('mutating');
    var lineEl = $('.acp__line[data-key="' + (window.CSS && CSS.escape ? CSS.escape(key) : key) + '"]');
    if (lineEl) lineEl.classList.add('is-busy');

    fetch(ROUTES.change + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, status: r.status, j: j }; }); })
      .then(function (res) {
        delete S.pendingMutations[key];
        if (id !== S.reqId) return;
        if (!res.ok) { fail(res.status === 422 ? 'stock' : 'unknown', res.j && res.j.description); return; }
        render(res.j, null);
        setState('open');
        syncBubble(res.j);
        say(quantity === 0 ? 'Item removed. Cart now has ' + res.j.item_count + ' items.'
                           : 'Quantity updated. Cart now has ' + res.j.item_count + ' items.');
        track(quantity === 0 ? 'cart_preview_line_removed' : 'cart_preview_qty_changed', { quantity: quantity });
      })
      .catch(function () { delete S.pendingMutations[key]; if (id === S.reqId) fail('network'); });
  }

  /* -------------------------------------------------- add interception */
  // A single channel: anything on the page that adds to the cart through the
  // Shopify AJAX route lands here, so Finder cards, Doctor verified parts and
  // the multi-item repair tray all get the same confirmed preview.
  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isAdd = /\/cart\/add(\.js)?(\?|$)/.test(url);
      var call = nativeFetch.apply(this, arguments);
      if (!isAdd) return call;

      setState('adding');
      return call.then(function (response) {
        // Never consume the caller's body — clone before reading.
        var clone = response.clone();
        clone.json().then(function (j) {
          if (!response.ok) {
            open();
            fail(response.status === 422 ? 'stock' : 'unknown', j && j.description);
            return;
          }
          var keys = [];
          if (j && j.items && j.items.length) keys = j.items.map(function (i) { return i.key; });
          else if (j && j.key) keys = [j.key];
          open();
          refresh(keys).then(function (cart) {
            if (!cart) return;
            say('Added to cart. ' + cart.item_count + (cart.item_count === 1 ? ' item' : ' items') + ' in your cart.');
            track('cart_preview_add_confirmed', { item_count: cart.item_count });
          });
        }).catch(function () { setState('idle'); });
        return response;
      });
    };
  }

  /* ------------------------------------------------------------------- wire */
  $$('[data-acp-close]').forEach(function (b) { b.addEventListener('click', close); });

  // Clicking the scrim closes; clicking inside the panel must not.
  var panelEl = $('#acp-panel');
  if (panelEl) panelEl.addEventListener('click', function (e) { e.stopPropagation(); });

  window.AresCartPreview = {
    open: open,
    close: close,
    refresh: refresh,
    state: function () { return S.state; },
    cart: function () { return S.cart; },
    hasAccelerated: HAS_ACCELERATED
  };
})();
